import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AlarmLevel,
    ComponentDefinition,
    DateRange,
    IApplicationConfiguration,
    IDatePickerDates,
    IDatePickerRange,
    IDateRangePickerOptions,
    IFrequency,
    IStringDateRange2,
    JobStatus,
    Notification,
    NotificationType,
    TimeUnit
} from '../../models';
import {
    IAuthenticationService,
    ICurrentApplicationService,
    IDateAlignmentHelperService,
    IDateHelperFormatService,
    IDateNavigationService,
    IDateRangeOptionsBuilder,
    IDateRangePickerService,
    IDateRangeService,
    IJobDefinitionService,
    ILoggerService,
    IMediaHelperService,
    INotificationHubService,
    IStoreService,
    IUserService,
} from '../../services';
import { formDirtyChanged, Helper, momentFormat, notificationReceived, rangeSelected, refreshClick } from '../../common';
import { toDuration } from '../../services/helpers/toDuration';
import { getTimeZone } from '../../common/getTimezone';
import { getSpecialRange } from './getSpecialRange';


class DateNavigationController implements angular.IComponentController, angular.IOnChanges {
    actualBeginDate: string;
    actualEndDate: string;
    beginDateDefaultInterval: string;
    canNavigate = true;
    datePicker: IDatePickerRange = { date: { startDate: null, endDate: null } };
    formatMoment: string = momentFormat;
    isBeginDateHidden: boolean;
    isDirty: boolean;
    isLoading: boolean; // Should be readonly 
    isMobile: boolean = false;
    isPickerOpen: boolean = false;
    isPlaying: boolean;
    isPlayingUsingJobEvent: boolean;
    lastRange: IDatePickerDates;
    loadingMessage: string;
    modalEmbedded: boolean;
    options: IDateRangePickerOptions;
    selectedDates: DateRange[];
    specialRange: string;
    timeZone: string;

    readonly alignBeginOnPrevious: boolean;
    readonly autoRefresh?: boolean;
    readonly frequency: IFrequency;
    readonly isDirtyPrevented = false;
    readonly isDisabled: boolean;
    readonly initialRange: IStringDateRange2;
    readonly isRefreshPrevented: boolean;
    readonly needsRefresh?: { isRequired: boolean; message: string; };
    readonly startAutoRefresh: boolean;

    private _config: IApplicationConfiguration;
    private _interval: angular.IPromise<boolean>;
    private _notificationGuid: string = Helper.getGuid();
    private _watchers: (() => void)[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        '$interval',
        'userService',
        'authenticationService',
        'dateAlignmentHelperService',
        'dateHelperFormatService',
        'dateRangeService',
        'loggerService',
        'dateNavigationService',
        'dateRangeOptionsBuilder',
        'currentApplicationService',
        'dateRangePickerService',
        'mediaHelperService',
        'jobDefinitionService',
        'notificationHubService',
        'storeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$q: angular.IQService,
        private _$interval: angular.IIntervalService,
        private _userService: IUserService,
        private _authenticationService: IAuthenticationService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateRangeService: IDateRangeService,
        private _loggerService: ILoggerService,
        private _dateNavigationService: IDateNavigationService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder,
        private _currentApplicationService: ICurrentApplicationService,
        private _dateRangePickerService: IDateRangePickerService,
        private _mediaHelper: IMediaHelperService,
        private _jobDefinitionService: IJobDefinitionService,
        private _notificationHubService: INotificationHubService,
        private _storeService: IStoreService
    ) {
        this._config = this._currentApplicationService.getApplicationConfiguration();
    }

    $onInit() {
        this.isPlaying = false;
        this.isDirty = false;
        this.modalEmbedded = false;
        this.selectedDates = [];
        this.isMobile = this._mediaHelper.isLargeMobile();

        if (!this.beginDateDefaultInterval) {
            this.beginDateDefaultInterval = 'e-7d';
        }
        if (!this.isBeginDateHidden) {
            this.isBeginDateHidden = false;
        }
        try {
            this.timeZone = getTimeZone();
        } catch (e) {
            this._loggerService.logError(e);
        }
        let beginDate: string;
        let endDate: string;
        if (this._dateRangeService.initialized()) {
            beginDate = this._dateRangeService.beginDate() as string;
            endDate = this._dateRangeService.endDate() as string;
        } else {
            beginDate = moment().subtract(7, 'd').toISOString();
            endDate = moment().toISOString();
        }

        this.initOptions();

        this._$scope.$on(formDirtyChanged, (_event: angular.IAngularEvent, data) => {
            if (!this.isDirtyPrevented) {
                this.canNavigate = !data;
            }
        });

        this._$scope.$on(rangeSelected, (_event: angular.IAngularEvent, data: DateRange) => {
            this.specialRange = data.keyword || data.comment;
            // #46870: needed to use local date converted from original time zone => .local()
            this.datePicker.date = this.getValidRange({
                startDate: moment.tz(data.beginDateTime, data.timeZone).local(),
                endDate: moment.tz(data.endDateTime, data.timeZone).local()
            });
            this.readData();
        });

        this._watchers = [
            this._$rootScope.$on('$translateChangeEnd', () => { this.initOptions(); }),
            this._$rootScope.$on(notificationReceived, (_event, n) => this.notification(n))
        ];

        this._$scope.$watch(
            () => this.initialRange,
            () => {
                if (this.initialRange?.beginDate || this.initialRange?.endDate) {
                    this.updateInitialRange();
                    this.lastRange = angular.copy(this.datePicker.date);
                }
            }
        );

        // Initialize default dates and last range
        const userName = this._authenticationService.authentication.userName;
        if (userName) {
            this.getLastRange();
        }
        else {
            this.datePicker.date = this.getValidRange({
                startDate: moment(beginDate),
                endDate: moment(endDate)
            });
        }
        this.lastRange = angular.copy(this.datePicker.date);
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['frequency'] && changes['frequency'].currentValue) {
            this.updateFrequency();
        }
        if (changes['startAutoRefresh']?.currentValue) {
            if (!this.isPlaying) {
                this.play();
            }
        }
    }

    $onDestroy() {
        try {
            if (this._interval) {
                this._$interval.cancel(this._interval);
            }
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach(w => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    private notification(notification: Notification) {
        const isPlayingForCurrentApplication = this.isPlaying
            && notification
            && notification.workEnvironment.applicationGuid === this._config.guid;
        const shouldRefreshForJob = isPlayingForCurrentApplication
            && notification.type === NotificationType.JobCompleted;
        const shouldRefreshForTaskEnd = isPlayingForCurrentApplication
            && notification.type === NotificationType.TaskCompleted
            && !this.isPlayingUsingJobEvent;
        if (shouldRefreshForJob || shouldRefreshForTaskEnd) {
            const endDate = notification.type === NotificationType.JobCompleted
                ? this.addOneBaseInterval(moment(notification.notificationDate))
                : moment(notification.notificationDate);
            this.syncDateAndRefresh(endDate);
        }
    }

    private updateFrequency() {
        this.initOptions();
        if (this.datePicker.date.startDate && this.datePicker.date.endDate) {
            this.readData();
        }
    }

    private updateInitialRange() {
        if (this.initialRange && (this.initialRange.beginDate || this.initialRange.endDate)) {
            if (this.initialRange.beginDate && this.initialRange.endDate) {
                this.datePicker.date = this.getValidRange({
                    startDate: moment(this.initialRange.beginDate),
                    endDate: moment(this.initialRange.endDate)
                });
            } else {
                if (this.initialRange.beginDate) {
                    this.datePicker.date.startDate = moment(this.initialRange.beginDate);
                }
                if (this.initialRange.endDate) {
                    this.datePicker.date.endDate = moment(this.initialRange.endDate);
                }
            }
            this.readData(false);
        }
    }

    private initOptions(): angular.IPromise<void> {
        const dateTimeFormat = this._dateHelperFormatService.getDateFormat();
        this.options = {
            locale: {
                format: dateTimeFormat
            }
        };

        return this._dateRangeOptionsBuilder
            .getDefaultOptions(this.frequency, this.isMobile)
            .then(options => {
                options.eventHandlers = {
                    'apply.daterangepicker': () => this.applyClick(options),
                    'outsideClick.daterangepicker': () => this.applyClick(options),
                    'show.daterangepicker': () => this.isPickerOpen = true,
                    'hide.daterangepicker': () => this.isPickerOpen = false,
                };
                options.locale.format = dateTimeFormat;
                this.options = options;
            });
    }

    refresh(save: boolean = true, force: boolean = false) {
        this.readData(save, force);
    }

    next() {
        this.specialRange = '';
        const interval = this.smartInterval();
        const actualBegin = this.datePicker.date.endDate;
        const actualEnd = this.datePicker.date.endDate
            .clone()
            .add(interval.interval, interval.unit as moment.unitOfTime.DurationConstructor);
        this.datePicker.date = {
            startDate: actualBegin,
            endDate: actualEnd
        };
        this.applyClick(this.options);
    }

    previous() {
        this.specialRange = '';
        const interval = this.smartInterval();
        const actualEnd = this.datePicker.date.startDate;
        const actualBegin = this.datePicker.date.startDate
            .clone()
            .subtract(interval.interval, interval.unit as moment.unitOfTime.DurationConstructor);
        this.datePicker.date = {
            startDate: actualBegin,
            endDate: actualEnd
        };
        this.applyClick(this.options);
    }

    play() {
        this.isPlaying = !this.isPlaying;
        if (this.isPlaying) {
            this.isLoading = true;

            this._jobDefinitionService
                .getAll()
                .then(jobs => {
                    // #36002 refresh to job last processed and wait for job event
                    // or if no job periodically refresh
                    const job = jobs.find(j => j.status !== JobStatus.Stopped && j.status !== JobStatus.Failed);
                    this.isPlayingUsingJobEvent = job !== undefined;
                    if (this.isPlayingUsingJobEvent) {
                        // add twice the interval to the lastProcessed to get next period after the next to process
                        const endDate = this.computeEndDate(job.lastProcessed);
                        this.syncDateAndRefresh(endDate);
                    }
                })
                .finally(() => {
                    // Always start loop on frequency
                    // Get app base freq
                    const config = this._currentApplicationService.getApplicationConfiguration();
                    const delay = this._dateNavigationService.getDelay(config.interval, config.intervalTimeUnit);
                    this.isLoading = false;
                    // Autorefresh on each freq
                    this.playingLoop()
                        .then(() => {
                            this._interval = this._$interval(() => this.playingLoop(), delay);
                        });
                });
        } else {
            this._$interval.cancel(this._interval);
        }
    }

    private computeEndDate(lastProcessed: string): moment.Moment {
        return '0001-01-01T00:00:00' === lastProcessed // job was never processed
            ? this._dateAlignmentHelperService.getPreviousSync(moment(), this._config)
            : this.addOneBaseInterval(this.addOneBaseInterval(moment(lastProcessed)));// Add 2 because getPrevious gets called below
    }

    private addOneBaseInterval(date: moment.Moment): moment.Moment {
        const duration = toDuration(this._currentApplicationService.getApplicationConfiguration());
        date = date.add(duration);
        return date;
    }

    private isSameRange(a: IDatePickerDates, b: IDatePickerDates) {
        return a && b
            && a.startDate?.isSame(b.startDate)
            && a.endDate?.isSame(b.endDate);
    }

    private getValidRange(dates: IDatePickerDates): IDatePickerDates {
        const freq = this.getFrequency();
        const range = this._dateRangePickerService.enforceAlign(dates, freq, this._config, this.alignBeginOnPrevious);
        // #36875 don't allow start and end date to be the same
        if (range.startDate.isSame(range.endDate)) {
            range.endDate.add(toDuration(freq));
        }

        // date picker does not seems to support other timezone, even if utc and tz are right
        // so range must be forced on local tz
        range.startDate = this.ensureLocal(range.startDate);
        range.endDate = this.ensureLocal(range.endDate);

        return range;
    }

    private ensureLocal(input: moment.Moment): moment.Moment {
        // by doing so we clear the tz info from the moment object and just use the plain utc date
        return moment(input.toISOString());
    }

    private getFrequency(): IFrequency {
        return this.frequency && this.frequency.interval > 0
            ? this.frequency
            : { interval: this._config.interval, intervalTimeUnit: this._config.intervalTimeUnit };
    }

    // End date from picker is always at 23:59:59
    // this trick is done so that hour is resetted
    // and next will compute next correct alignemet given the freq
    // /!\ it should only be called when applying date from picker click, not internal
    private cleanupDatePickerDate(range: IDatePickerDates): IDatePickerDates {
        const end = range.endDate.clone();
        if (this.getFrequency().intervalTimeUnit >= TimeUnit.Day) {
            end.startOf('d');
        }

        return {
            startDate: range.startDate.clone(),
            endDate: end
        };
    }

    private applyClick(options: IDateRangePickerOptions): void {
        const range = this.getValidRange(this.cleanupDatePickerDate(this.datePicker.date));
        const isSame = this.isSameRange(range, this.datePicker.date);
        const isLastRange = this.isSameRange(range, this.lastRange);

        // #37078 if range is the same, dont do anything more
        if (!isLastRange || !isSame) {
            this.lastRange = angular.copy(range);
            this.datePicker.date = range;
            this.specialRange = getSpecialRange(this.datePicker.date, options);
            if (!isLastRange) {
                this.readData(true);
            }
        }
    }
    
    private playingLoop(): angular.IPromise<void> {
        this.checkLiveConnection();

        return this.isPlayingUsingJobEvent
            ? this._$q.when()
            : this.syncDateAndRefresh();
    }

    private async checkLiveConnection() {
        if (!this._notificationHubService.isConnected()) {
            this._storeService.dispatchers().globalNotifications.addOne({
                guid: this._notificationGuid,
                icon: 'fa-exclamation',
                sref: '#',
                status: AlarmLevel.Critical,
                text: 'Connection lost. Dashboard is currently unable to refresh itself.',
                tooltip: 'Refresh the page to try to reinitialize the connection manually.'
            });
            try {
                await this._notificationHubService
                    .connect()
                    .then(() => {
                        this._storeService.dispatchers().globalNotifications.removeOne(this._notificationGuid);
                    });
            } catch (e) {
                this._loggerService.logWarning('Unable to restore SignalR connection', e);
            }
        }
    }

    private syncDateAndRefresh(endDate: moment.Moment = moment()): angular.IPromise<void> {
        const interval = this.smartInterval();
        return this._dateAlignmentHelperService
            .getPrevious(endDate)
            .then(previousEndDate => {
                const beginDate = previousEndDate
                    .clone()
                    .subtract(interval.interval, interval.unit as moment.unitOfTime.DurationConstructor);
                this.datePicker.date = {
                    startDate: beginDate.milliseconds(0),
                    endDate: previousEndDate.milliseconds(0)
                };
                this.refresh(true, true);
            });
    }

    private smartInterval(): { interval: number, unit: string } {
        const milliHour = 1000 * 60 * 60;
        const begin = this.datePicker.date.startDate.toDate();
        const end = this.datePicker.date.endDate.toDate();

        const keepMinute = begin.getMinutes() === end.getMinutes();
        const keepHour = begin.getHours() === end.getHours() && keepMinute;
        const keepDay = begin.getDate() === end.getDate() && keepHour;
        const keepMonth = begin.getMonth() === end.getMonth() && begin.getFullYear() !== end.getFullYear()
            && keepDay;

        let interval = this.getInterval();
        let unit = 'ms';

        if (keepMonth) {
            interval = Math.round(interval / (milliHour * 24 * 365));
            unit = 'y';
        } else if (keepDay) {
            interval = Math.round(interval / (milliHour * 24 * 31));
            unit = 'M';
        } else if (keepHour) {
            interval = Math.round(interval / (milliHour * 24));
            unit = 'd';
        } else if (keepMinute) {
            interval = Math.round(interval / (milliHour));
            unit = 'h';
        }

        return { interval: interval, unit: unit };
    }

    private getLastRange() {
        if (!this.initialRange || !this.initialRange.beginDate || !this.initialRange.endDate) {
            this.getSelectedDates()
                .then(() => {
                    const range = _.first(this.selectedDates);
                    if (range) {
                        if (!this.initialRange) {
                            this.datePicker.date = this.getValidRange({
                                startDate: this.getLocalMoment(range.beginDateTime, range.timeZone),
                                endDate: this.getLocalMoment(range.endDateTime, range.timeZone)
                            });
                            this.readData(false);
                        }
                    }
                });
        }
    }

    private getLocalMoment(date: string, timeZone: string): moment.Moment {
        return moment(moment.tz(date, timeZone).toDate());
    }

    private getInterval(): number {
        return this.datePicker.date.endDate.diff(this.datePicker.date.startDate);
    }

    private setRanges() {
        this.actualBeginDate = this.datePicker.date.startDate.toISOString();
        this.actualEndDate = this.datePicker.date.endDate.toISOString();
        this._dateRangeService.beginDate(this.actualBeginDate);
        this._dateRangeService.endDate(this.actualEndDate);
        this._dateRangeService.initialized(true);
    }


    private readData(save: boolean = true, force: boolean = false) {
        this.setRanges();
        if (!this.isRefreshPrevented) {
            this._$rootScope.$broadcast(refreshClick, {
                beginDate: this.actualBeginDate,
                endDate: this.actualEndDate,
                force: force
            });
            this.isDirty = false;
            if (save) {
                const data = this.getRange();
                const userName = this._authenticationService.authentication.userName;
                this._userService
                    .addSelecteddDatesForUser(userName, data)
                    .then(() => this.getSelectedDates());
            }
        }
    }

    private getSelectedDates(): angular.IPromise<DateRange[]> {
        const deferred = this._$q.defer<DateRange[]>();
        this._dateNavigationService
            .getLastRanges()
            .then(data => {
                this.selectedDates = data;
                return deferred.resolve(data);
            })
            .catch(reason => deferred.reject(reason));
        return deferred.promise;
    }

    private getRange() {
        const beginMoment = this.datePicker.date.startDate;
        const endMoment = this.datePicker.date.endDate;
        const bd = beginMoment.format(this.formatMoment);
        const ed = endMoment.format(this.formatMoment);
        const range = new DateRange();
        range.beginDateTime = bd;
        range.endDateTime = ed;
        range.timeZone = this.timeZone;
        return range;
    }
}

export const wtDateNavigation: ComponentDefinition = {
    componentId: 'wtDateNavigation',
    controller: DateNavigationController,
    template: require('./dateNavigation.component.html'),
    controllerAs: 'vm',
    transclude: true,
    bindings: {
        actualBeginDate: '=?',
        actualEndDate: '=?',
        alignBeginOnPrevious: '<?',
        autoRefresh: '<?',
        beginDateDefaultInterval: '@?',
        frequency: '<?',
        initialRange: '<?',
        isBeginDateHidden: '=?',
        isDirtyPrevented: '<?',
        isDisabled: '<?',
        isLoading: '<?',
        isPlaying: '=?',
        isRefreshPrevented: '<?',
        modalEmbedded: '@?',
        needsRefresh: '<?',
        specialRange: '=?',
        startAutoRefresh: '<?'
    }
};